import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DocumentUploadProps {
  onClose: () => void;
  patientCnp?: string;
}

interface FileUpload {
  type: 'scrisoare' | 'histopatologic' | 'analize' | 'alte';
  file: File | null;
}

export default function DocumentUpload({ onClose, patientCnp }: DocumentUploadProps) {
  const [files, setFiles] = useState<FileUpload[]>([
    { type: 'scrisoare', file: null },
    { type: 'histopatologic', file: null },
    { type: 'analize', file: null },
    { type: 'alte', file: null }
  ]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (type: FileUpload['type'], file: File | null) => {
    setFiles(prev => prev.map(f => 
      f.type === type ? { ...f, file } : f
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setUploading(true);

    let uploadCnp: string;

    try {
      if (patientCnp) {
        // If CNP is provided (doctor uploading), use it directly
        uploadCnp = patientCnp;
      } else {
        // Get user's CNP (patient uploading)
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Utilizatorul nu este autentificat');

        const { data: profile } = await supabase
          .from('profiles')
          .select('cnp')
          .eq('id', user.id)
          .single();

        if (!profile?.cnp) throw new Error('CNP nu a fost găsit');
        uploadCnp = profile.cnp;
      }

      // Upload files that were selected
      const selectedFiles = files.filter(f => f.file);
      if (selectedFiles.length === 0) {
        throw new Error('Vă rugăm să selectați cel puțin un fișier');
      }

      // Upload files to Supabase storage
      const uploads = await Promise.all(
        selectedFiles.map(async ({ type, file }) => {
          if (!file) return null;
          
          const fileName = `${uploadCnp}/${type}_${file.name}`;
          
          const { error: uploadError } = await supabase.storage
            .from('medical-documents')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;
          return fileName;
        })
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Încărcarea a eșuat');
    } finally {
      setUploading(false);
    }
  };

  const documentTypes = {
    scrisoare: 'Scrisoare medicală',
    histopatologic: 'Examen histopatologic',
    analize: 'Analize de sânge',
    alte: 'Alte documente'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              Încărcare Documente
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {files.map(({ type, file }) => (
              <div key={type} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {documentTypes[type]}
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => handleFileChange(type, e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-[#024994] file:text-white
                    hover:file:bg-[#024994]"
                />
                {file && (
                  <p className="text-sm text-gray-500">
                    Fișier selectat: {file.name}
                  </p>
                )}
              </div>
            ))}

            {error && (
              <p className="text-red-600 text-sm">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                disabled={uploading}
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="px-4 py-2 bg-[#024994] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Se încarcă...' : 'Încarcă'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}